'use client';

import Link from 'next/link';
import { MapPin } from 'lucide-react';
import Container from '@/components/ui/container';
import { parseHeading } from '@/lib/utils';

const DEFAULT_AREAS = [
	{ name: 'Lansingerland', slug: 'lansingerland' },
	{ name: 'Bergschenhoek', slug: 'bergschenhoek' },
	{ name: 'Berkel en Rodenrijs', slug: 'berkel-en-rodenrijs' },
	{ name: 'Bleiswijk', slug: 'bleiswijk' },
	{ name: 'Rotterdam', slug: 'rotterdam' },
	{ name: 'Zoetermeer', slug: 'zoetermeer' },
	{ name: 'Delft', slug: 'delft' },
	{ name: 'Pijnacker', slug: 'pijnacker' },
];

interface ServiceAreaProps {
	data: {
		title?: string;
		description?: string;
		areas?: {
			name: string;
			slug: string;
		}[];
	};
}

export default function ServiceArea({ data }: ServiceAreaProps) {
	const title = data?.title || 'Actief in uw regio';
	const description =
		data?.description ||
		'Wij zijn voornamelijk actief in Lansingerland en omstreken, maar ook daarbuiten staan we voor u klaar.';
	const areas = Array.isArray(data?.areas) && data.areas.length > 0 ? data.areas : DEFAULT_AREAS;

	return (
		<section className="py-16 bg-blue-dark text-white relative overflow-hidden">
			{/* Background Pattern (Optional) */}
			<div className="absolute inset-0 opacity-5 pointer-events-none">
				<div className="absolute top-0 left-0 size-64 bg-white rounded-full blur-3xl transform -translate-x-1/2 -translate-y-1/2" />
				<div className="absolute bottom-0 right-0 size-96 bg-orange rounded-full blur-3xl transform translate-x-1/2 translate-y-1/2" />
			</div>

			<Container className="relative z-10">
				<div className="flex flex-col lg:flex-row gap-12 items-center">
					{/* Left: Content */}
					<div className="w-full lg:w-1/2 space-y-6">
						<div className="inline-flex items-center space-x-2 bg-white/10 px-4 py-1.5 rounded-full text-sm font-medium text-orange-light border border-white/5">
							<MapPin className="size-4" />
							<span>Lokaal & Betrouwbaar</span>
						</div>

						<h2 className="text-3xl md:text-4xl font-heading leading-tight">{parseHeading(title)}</h2>

						<p className="text-lg text-blue-light/80 max-w-xl">{description}</p>

						<div className="pt-4">
							<Link
								href="/contact"
								className="inline-block bg-orange hover:bg-orange-hover text-white font-bold py-3 px-8 rounded-md transition-colors shadow-lg"
							>
								Neem contact op
							</Link>
						</div>
					</div>

					{/* Right: Area List / Map visual */}
					<div className="w-full lg:w-1/2">
						<div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10">
							<h3 className="text-xl mb-6 border-b border-white/10 pb-4">Wij installeren o.a. in:</h3>
							<div className="grid grid-cols-2 gap-4">
								{areas.map((area, idx) => (
									<Link
										key={idx}
										href={`/werkgebied/${area.slug}`}
										className="flex items-center space-x-2 text-blue-light hover:text-white transition-colors group"
									>
										<div className="size-1.5 rounded-full bg-orange opacity-60 group-hover:opacity-100 transition-opacity" />
										<span>{area.name}</span>
									</Link>
								))}
							</div>
							<div className="mt-6 pt-4 border-t border-white/10 text-sm text-blue-light/60">
								* Staat uw woonplaats er niet tussen? Neem gerust contact op.
							</div>
						</div>
					</div>
				</div>
			</Container>
		</section>
	);
}
